/** Shared helpers for staff notification inbox rows (header bell + inbox page). */

export function apiOk(res) {
  return res && (res.success === 1 || res.success === "1");
}

export function readIsReadFlag(row) {
  if (!row) return false;
  return !!(row.IsRead ?? row.isRead);
}

export function rowId(row) {
  return String(row?.Id || row?.id || "").trim();
}

export function rowMailboxKind(row) {
  return String(row?.MailboxRowKind || row?.mailboxRowKind || "Inbound");
}

export function rowSubject(row) {
  return String(row?.Subject || row?.subject || "").trim() || "(No subject)";
}

export function rowFrom(row) {
  const from =
    row?.From ??
    row?.from ??
    row?.FromAddress ??
    row?.fromAddress ??
    "";
  return String(from).trim() || "—";
}

export function rowTo(row) {
  const to =
    row?.To ??
    row?.to ??
    row?.ToAddress ??
    row?.toAddress ??
    "";
  return String(to).trim();
}

/** Sent / outbound rows should show the real recipient instead of "to me". */
export function rowIsSentMailboxRow(row, sentRows) {
  if (!row) return false;
  if (rowMailboxKind(row).toLowerCase() === "outbound") return true;
  return rowIsInSentList(row, sentRows);
}

export function resolveSentRecipientDisplay(row, headerFields) {
  const fromHeader = String(
    headerFields?.ToAddress ?? headerFields?.toAddress ?? "",
  ).trim();
  const fromRow = rowTo(row);
  return fromHeader || fromRow || "—";
}

/** Known noreply senders for system outbound mail (CAPA, etc.). */
export const NOREPLY_FROM_ADDRESSES = new Set([
  "noreply@aq-trials.com",
  "noreply@aqtrials.com",
]);

/** @deprecated kept for older imports — AdminRole is not required for System Sent. */
export const ADMIN_ROLE_PERMISSION_KEYS = ["AdminRole", "ADMIN_ROLE"];

export function normalizeMailboxEmail(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const angle = s.match(/<([^>]+)>/);
  const candidate = (angle ? angle[1] : s).trim();
  const em = candidate.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/);
  return (em ? em[0] : candidate).trim().toLowerCase();
}

/** @deprecated use canViewSystemSent — AdminRole is not on System Admin permission strings. */
export function hasAdminRolePermission(rawPermissionString) {
  const keys = String(rawPermissionString || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  return ADMIN_ROLE_PERMISSION_KEYS.some((key) => keys.includes(key));
}

/**
 * System Sent (org noreply / platform outbound) — System Administrator only.
 * Do not also require AdminRole: login expands System Admin to getAllPermissionKeys(),
 * which never includes the non-assignable AdminRole key (that was hiding system mail).
 */
export function canViewSystemSent(isSystemAdmin) {
  return !!isSystemAdmin;
}

/** @deprecated alias — use canViewSystemSent */
export function canViewOrgNoreplyOutboundInSent({ isSystemAdmin }) {
  return canViewSystemSent(isSystemAdmin);
}

export function isNoreplyOutboundRow(row) {
  if (rowMailboxKind(row).toLowerCase() !== "outbound") return false;
  const from = normalizeMailboxEmail(rowFrom(row));
  return NOREPLY_FROM_ADDRESSES.has(from);
}

export function rowIsInboundFromStaff(row, staffEmail) {
  if (rowMailboxKind(row).toLowerCase() !== "inbound") return false;
  const userFrom = normalizeMailboxEmail(staffEmail);
  if (!userFrom) return false;
  return normalizeMailboxEmail(rowFrom(row)) === userFrom;
}

/**
 * Password / access-code / OTP mails are ACS outbound with Module=UserManagement.
 * Keep real email delivery; hide only from in-app My Inbox / badges.
 */
export function isCredentialSecurityMailboxRow(row) {
  if (!row) return false;
  if (rowMailboxKind(row).toLowerCase() !== "outbound") return false;
  const module = String(row?.Module || row?.module || "")
    .trim()
    .toLowerCase();
  return module === "usermanagement";
}

/** Inbox = received mail only (exclude self-sent inbound + credential security mail). */
export function buildInboxRows(receivedRows, staffEmail) {
  return (receivedRows || []).filter(
    (r) =>
      !rowIsInboundFromStaff(r, staffEmail) &&
      !isCredentialSecurityMailboxRow(r),
  );
}

export function mergeMailboxRows(receivedRows, sentRows) {
  const map = new Map();
  for (const r of receivedRows || []) {
    const id = rowId(r);
    if (id) map.set(`${rowMailboxKind(r)}:${id}`, r);
  }
  for (const r of sentRows || []) {
    const id = rowId(r);
    if (id) map.set(`${rowMailboxKind(r)}:${id}`, r);
  }
  return [...map.values()];
}

export function rowIsInSentList(row, sentRows) {
  if (!row || !Array.isArray(sentRows)) return false;
  const id = rowId(row);
  if (!id) return false;
  const kind = rowMailboxKind(row);
  return sentRows.some((r) => rowId(r) === id && rowMailboxKind(r) === kind);
}

export function rowPreview(row) {
  return String(
    row?.TextBodyPreview ||
      row?.textBodyPreview ||
      row?.HtmlBodyPreview ||
      row?.htmlBodyPreview ||
      "",
  ).trim();
}

export function rowReceived(row) {
  return row?.ReceivedUtc || row?.receivedUtc || null;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatTwelveFromParts(day, month, year, h24, minutes) {
  const tt = h24 >= 12 ? "PM" : "AM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${pad2(day)}/${pad2(month)}/${year} ${pad2(h12)}:${pad2(minutes)} ${tt}`;
}

function formatTwelveFromUtcDate(d) {
  if (Number.isNaN(d.getTime())) return "—";
  return formatTwelveFromParts(
    d.getUTCDate(),
    d.getUTCMonth() + 1,
    d.getUTCFullYear(),
    d.getUTCHours(),
    d.getUTCMinutes(),
  );
}

export function formatMailboxDateTime(iso) {
  const s = String(iso ?? "").trim();
  if (!s) return "—";

  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/,
  );
  if (m) {
    const [, y, mo, d, h, mi] = m;
    return formatTwelveFromParts(Number(d), Number(mo), Number(y), Number(h), Number(mi));
  }

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    if (/Z|[+-]\d{2}/.test(s)) {
      return formatTwelveFromUtcDate(parsed);
    }
    return formatTwelveFromParts(
      parsed.getDate(),
      parsed.getMonth() + 1,
      parsed.getFullYear(),
      parsed.getHours(),
      parsed.getMinutes(),
    );
  }
  return s;
}

export function rowReceivedDisplay(row) {
  if (!row) return "—";
  const pre = row.ReceivedAtDisplay ?? row.receivedAtDisplay;
  if (pre != null && String(pre).trim() !== "") return String(pre).trim();
  return formatMailboxDateTime(rowReceived(row));
}

export function sortInboxRowsNewestFirst(rows) {
  return [...rows].sort((a, b) => {
    const ta = new Date(rowReceived(a) || 0).getTime();
    const tb = new Date(rowReceived(b) || 0).getTime();
    return tb - ta;
  });
}

export function unreadInboxRows(rows) {
  return sortInboxRowsNewestFirst(rows.filter((r) => !readIsReadFlag(r)));
}
